<template>
  <div
    v-if="isPrintDisplayMode && includeAllImagesInPdf"
    class="relative overflow-hidden c-carousel flex flex-col gap-4"
  >
    <CImage
      v-for="src in images"
      :key="src"
      class="rounded-2 w-full h-full"
      :img-props="{ class: 'carousel-img w-full h-full' }"
      :lazy="isDefaultDisplayMode"
      object-fit="contain"
      :preview-disabled="previewDisabled"
      :src="src"
      @error="onErrorImage(images[currentSlideIndex])"
      @load="onLoadImage"
    />
  </div>

  <div
    v-else
    class="relative overflow-hidden c-carousel flex"
    :style="{
      height: (imageRef?.$el as any)?.height,
      minHeight: placeholderHeight || previousImageHeight,
      backgroundColor: style?.backgroundColor,
    }"
  >
    <transition
      :enter-active-class="'slide-fade-enter-active'"
      :enter-class="enterFromClass"
      :leave-active-class="'slide-fade-leave-active'"
      :leave-to-class="leaveToClass"
      mode="out-in"
      :name="slideDirection === 'next' ? 'slide-fade-next' : 'slide-fade-prev'"
      tag="div"
    >
      <CImage
        v-if="images[currentSlideIndex]"
        :key="currentSlideIndex"
        ref="imageRef"
        class="rounded-2 w-full h-full"
        :img-props="{ class: 'carousel-img w-full h-full' }"
        :lazy="isDefaultDisplayMode"
        object-fit="contain"
        :preview-disabled="previewDisabled"
        :src="images[currentSlideIndex]"
        :style="{
          marginBottom: style?.paddingBottom ?? '0',
          marginLeft: style?.paddingLeft ?? '0',
          marginTop: style?.paddingTop ?? '0',
          marginRight: style?.paddingRight ?? '0',
          height: brokenImages[images[currentSlideIndex]] ? previousImageHeight : 'auto',
        }"
        @error="onErrorImage(images[currentSlideIndex])"
        @load="onLoadImage"
      />
    </transition>
    <div
      v-if="isDefaultDisplayMode"
      class="flex absolute justify-between pa-[10px] c-carousel__controls"
      :style="{
        bottom: style?.paddingBottom ?? 0,
        left: style?.paddingLeft ?? 0,
        right: style?.paddingRight ?? 0,
      }"
    >
      <div class="c-carousel__dots flex items-center">
        <div
          v-for="(_image, index) in images"
          :key="index"
          class="h-2 w-2 c-carousel__dot"
          :class="{ 'c-carousel__dot--active n-carousel__dot--active': currentSlideIndex === index }"
          @click="goToSlide(index)"
        ></div>
      </div>
      <div class="c-carousel__arrow-group flex gap-2">
        <CIcon class="cursor-pointer rounded-2 pa-[6px] c-carousel__arrow" icon="angle-left" @click="prevSlide" />
        <CIcon class="cursor-pointer rounded-2 pa-[6px] c-carousel__arrow" icon="angle-right" @click="nextSlide" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, nextTick, ref, watch, type CSSProperties } from 'vue'

import CImage from '@lib/components/CImage/CImage.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'

const { isDefaultDisplayMode, isPrintDisplayMode } = useCanvas()
const themeVars = useThemeVars()

const props = withDefaults(
  defineProps<{
    images: string[]
    placeholderHeight?: string
    previewDisabled?: boolean
    includeAllImagesInPdf?: boolean
    style?: CSSProperties
  }>(),
  {
    placeholderHeight: '500px',
    previewDisabled: false,
    includeAllImagesInPdf: true,
  },
)

const imageRef = ref<InstanceType<typeof CImage> | null>(null)
const previousImageHeight = ref(props.placeholderHeight)
const currentSlideIndex = ref(0)
const brokenImages = ref<Record<string, boolean>>({})
const slideDirection = ref('next')

const enterFromClass = computed(() =>
  slideDirection.value === 'next' ? 'slide-fade-enter-from-next' : 'slide-fade-enter-from-prev',
)
const leaveToClass = computed(() =>
  slideDirection.value === 'next' ? 'slide-fade-leave-to-next' : 'slide-fade-leave-to-prev',
)

function prevSlide() {
  updateHeightAndScroll()
  slideDirection.value = 'prev'
  currentSlideIndex.value = (currentSlideIndex.value - 1 + props.images.length) % props.images.length
}

function nextSlide() {
  updateHeightAndScroll()
  slideDirection.value = 'next'
  currentSlideIndex.value = (currentSlideIndex.value + 1) % props.images.length
}

function scrollToCarousel() {
  imageRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function goToSlide(index: number) {
  updateHeightAndScroll()
  slideDirection.value = index > currentSlideIndex.value ? 'next' : 'prev'
  currentSlideIndex.value = index
}

function updateHeightAndScroll() {
  const image = imageRef.value?.$el.querySelector('.carousel-img')

  previousImageHeight.value = image?.clientHeight !== 0 ? `${image?.clientHeight}px` : previousImageHeight.value
  scrollToCarousel()
}

async function onLoadImage(e: Event) {
  await nextTick()
  const target = e.target as HTMLElement | undefined

  previousImageHeight.value = `${target?.clientHeight}px`
}

function onErrorImage(image: string) {
  console.error('Error loading image: img url', props.images[currentSlideIndex.value])
  brokenImages.value[image] = true
}

watch(
  () => props.images,
  (images) => {
    if (!images?.length) return
    if (currentSlideIndex.value > images.length - 1) currentSlideIndex.value = 0
  },
  { immediate: true },
)
</script>
<style lang="scss" scoped>
.c-carousel {
  &__arrow {
    color: #eee;
    background-color: rgba(0, 0, 0, 0.3);
    transition: background-color 0.2s;
    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
  &__dot {
    margin: 0 5px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.6);
    transition:
      box-shadow 0.3s 0,
      background-color 0.3s 0;

    &--active {
      height: 10px;
      width: 10px;
      background-color: v-bind('themeVars.primary');
    }
  }
}
.slide-fade-enter-active {
  transition: all 0.5s ease-out;
  opacity: 0.5s;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  opacity: 0.5s;
}

.slide-fade-enter-from-next,
.slide-fade-leave-to-prev {
  transform: translateX(50%);
  opacity: 0;
}

.slide-fade-enter-from-prev,
.slide-fade-leave-to-next {
  transform: translateX(-50%);
  opacity: 0;
}
</style>
